import React, { startTransition, useState, useEffect, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Box } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';

import { Column, Row, TitleLarge } from '@dh/styles';
import { shouldForwardProps } from '@dh/utils';
import { DragIndicatorIcon, IconButton } from '@dh/components';

// Icons as constants to prevent recreation on each render
const ARROW_UP_ICON = <ArrowDropUp />;
const ARROW_DOWN_ICON = <ArrowDropDown />;
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    closestCenter,
    defaultDropAnimation,
    DndContext,
    DragEndEvent,
    DragMoveEvent,
    DragOverlay,
    DragStartEvent,
    DropAnimation,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    UniqueIdentifier,
    useSensor,
    useSensors
} from '@dnd-kit/core';

type OrderedListProps<T> = {
    items: T[] | undefined;
    getItemKey: (item: T) => string;
    renderItem: (item: T) => React.ReactNode;
    iconUp?: React.ReactNode;
    iconDown?: React.ReactNode;
    onOrderChanged?: (items: T[]) => void;
    showIndexNumbers?: boolean;
};

export const ItemContainer = styled(Row, shouldForwardProps)<{
    isDragging?: boolean;
    isOver?: boolean;
    grabbing?: boolean;
    isJustDropped?: boolean;
}>`
    padding: ${({ theme }) => theme.spacing(2)};
    border-radius: ${({ theme }) => theme.spacing(0.75)};
    border: ${({ theme, isDragging, grabbing, isJustDropped }) =>
        isDragging || grabbing || isJustDropped
            ? `2px solid ${theme.colors.primary}`
            : `1px solid ${theme.colors.outline}`};
    background: ${({ theme, isDragging, isOver, grabbing, isJustDropped }) =>
        isDragging || grabbing || isJustDropped
            ? theme.colors.primaryContainer
            : isOver
              ? theme.colors.primaryContainer
              : theme.colors.white};
    transition: all 0.3s ease;
    box-shadow: ${({ grabbing }) => (grabbing ? '0 8px 16px rgba(0, 0, 0, 0.2)' : 'none')};
    opacity: ${({ isDragging, grabbing }) => (isDragging && !grabbing ? 0.4 : 1)};
    border-style: ${({ isDragging }) => (isDragging ? 'dashed' : 'solid')};
    transform: ${({ grabbing, isJustDropped }) =>
        grabbing ? 'scale(1.02)' : isJustDropped ? 'scale(1.05)' : 'scale(1)'};
`;

type ItemProps = {
    children: React.ReactNode;
    tabIndex?: number;
    id: UniqueIdentifier;
    move?: (id: UniqueIdentifier, direction: 'up' | 'down') => void;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    grabbing?: boolean;
    showIndexNumbers: boolean;
    isJustDropped?: boolean;
};
const Item = React.memo<ItemProps>(({
    children,
    tabIndex,
    id,
    move,
    canMoveUp,
    canMoveDown,
    showIndexNumbers,
    grabbing = false,
    isJustDropped = false
}: ItemProps) => {
    const { listeners, setNodeRef, attributes, transform, transition, isDragging, isOver } =
        useSortable({
            id
        });

    // Memoize style object
    const style = useMemo(
        () => ({
            cursor: grabbing ? 'grabbing' : 'move',
            transform: CSS.Transform.toString(transform),
            scale: CSS.Scale.toString(transform),
            transition
        }),
        [grabbing, transform, transition]
    );

    // Memoize arrow button handlers
    const handleMoveUp = useCallback(() => {
        if (move) move(id, 'up');
    }, [move, id]);

    const handleMoveDown = useCallback(() => {
        if (move) move(id, 'down');
    }, [move, id]);

    return (
        <Row gap={2}>
            {showIndexNumbers && tabIndex !== undefined && (
                <TitleLarge>{`${tabIndex + 1}.`}</TitleLarge>
            )}
            <ItemContainer
                tabIndex={tabIndex}
                aria-roledescription='draggable'
                isDragging={isDragging}
                isOver={isOver}
                grabbing={grabbing}
                isJustDropped={isJustDropped}
            >
                <Row style={style} ref={setNodeRef} {...attributes} {...listeners} gap={2}>
                    <DragIndicatorIcon />
                    <Box sx={{ width: '100%' }}>{children}</Box>
                </Row>
                {move && (
                    <Row>
                        <IconButton
                            icon={ARROW_UP_ICON}
                            onClick={handleMoveUp}
                            disabled={!canMoveUp}
                        />
                        <IconButton
                            icon={ARROW_DOWN_ICON}
                            onClick={handleMoveDown}
                            disabled={!canMoveDown}
                        />
                    </Row>
                )}
            </ItemContainer>
        </Row>
    );
});

Item.displayName = 'Item';

export const DHOrderedList = <T extends { id: UniqueIdentifier }>({
    items: initialItems,
    renderItem,
    getItemKey,
    onOrderChanged,
    showIndexNumbers = false
}: OrderedListProps<T>) => {
    const [items, setItems] = useState<T[]>(initialItems ?? []);
    const [active, setActive] = useState<T | null>(null);
    const [activeOverIndex, setActiveOverIndex] = useState<number | undefined>(undefined);
    const [justDroppedId, setJustDroppedId] = useState<UniqueIdentifier | null>(null);

    const sensors = useSensors(
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
        useSensor(PointerSensor),
        useSensor(TouchSensor)
    );

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            const { active } = event;
            const item = items.find((item) => getItemKey(item) === active.id) ?? null;
            setActive(item);
        },
        [items, getItemKey]
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (active.id !== over?.id) {
                let newItems: T[];
                setItems((items) => {
                    const oldIndex = items.findIndex((item) => getItemKey(item) === active.id);
                    const newIndex = items.findIndex((item) => getItemKey(item) === over?.id);
                    newItems = arrayMove(items, oldIndex, newIndex);
                    return newItems;
                });

                // Trigger drop highlight animation
                setJustDroppedId(active.id);

                startTransition(() => onOrderChanged?.(newItems));
            }

            setActive(null);
            setActiveOverIndex(undefined);
        },
        [getItemKey, onOrderChanged]
    );

    // Reset the drop highlight after animation
    useEffect(() => {
        if (justDroppedId) {
            const timer = setTimeout(() => {
                setJustDroppedId(null);
            }, 600); // 600ms to match transition duration (300ms) + extra time for visibility

            return () => clearTimeout(timer);
        }
    }, [justDroppedId]);

    const handleDragMove = useCallback(
        (event: DragMoveEvent) => {
            const { over } = event;
            const overIndex = items.findIndex((item) => getItemKey(item) === over?.id);
            setActiveOverIndex(overIndex);
        },
        [items, getItemKey]
    );

    const move = useCallback(
        (id: UniqueIdentifier, direction: 'up' | 'down') => {
            const from = items.findIndex((item) => getItemKey(item) === id);
            const to = direction === 'up' ? from - 1 : from + 1;
            const newItems = arrayMove(items, from, to);
            setItems(newItems);
            startTransition(() => onOrderChanged?.(newItems));
        },
        [items, getItemKey, onOrderChanged]
    );

    const dropAnimation: DropAnimation = useMemo(
        () => ({
            keyframes({ transform }) {
                return [
                    { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
                    {
                        opacity: 0,
                        transform: CSS.Transform.toString({
                            ...transform.final,
                            x: transform.final.x + 5,
                            y: transform.final.y
                        })
                    }
                ];
            },
            easing: 'ease-out',
            sideEffects({ active }) {
                active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
                    duration: defaultDropAnimation.duration,
                    easing: defaultDropAnimation.easing
                });
            }
        }),
        []
    );

    return (
        <Column gap={1}>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragMove={handleDragMove}
            >
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    {items.map((item, index) => {
                        const id = getItemKey(item);
                        return (
                            <Item
                                tabIndex={index}
                                key={id}
                                id={id}
                                move={move}
                                canMoveUp={index > 0}
                                canMoveDown={index < items.length - 1}
                                showIndexNumbers={showIndexNumbers}
                                isJustDropped={justDroppedId === id}
                            >
                                {renderItem(item)}
                            </Item>
                        );
                    })}
                </SortableContext>
                <DragOverlay dropAnimation={dropAnimation}>
                    {active ? (
                        <Item
                            id={getItemKey(active)}
                            grabbing
                            showIndexNumbers={showIndexNumbers}
                            tabIndex={activeOverIndex}
                        >
                            {renderItem(active)}
                        </Item>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </Column>
    );
};
